import { Button, Css, useTestIds } from "@homebound/beam";
import { StepActions } from "src/components/stepper/StepActions";
import { useStepperContext } from "src/components/stepper/StepperContext";

type StepNavProps = {
  /** URL to navigate to when the user cancels out of the flow. */
  cancelUrl: string;
  /** Disables the primary (Next/Save) button, e.g. when the form is invalid. */
  disabled?: boolean;
  /**
   * Runs the shared `save()` and owns post-save navigation. Used by both "Save & exit" (first step)
   * and the final "Save" button.
   */
  onSave: () => Promise<void>;
  /** Label for the final step's primary save button (e.g. "Create" vs "Save Options"). */
  saveLabel?: string;
};

/**
 * Renders the stepper's footer actions into the stepper bar via `StepActions`.
 *
 * - First step (Details): Cancel · Save & exit · Next
 * - Last step (Variants): Cancel · Back · Save
 *
 * The stepper uses a single shared formState, so both "Save & exit" and "Save" call the same
 * `onSave`, which owns navigation to the new rpog/draft URL.
 */
export function StepNav(props: StepNavProps) {
  const { cancelUrl, disabled, onSave, saveLabel = "Save Options" } = props;
  const { nextStep, prevStep, isLastStep } = useStepperContext();
  const tid = useTestIds({}, "stepNav");

  return (
    <StepActions>
      <div css={Css.df.aic.gap1.$}>
        <Button size="lg" label="Cancel" variant="tertiary" onClick={cancelUrl} {...tid.cancel} />
        {isLastStep ? (
          <>
            <Button size="lg" label="Back" variant="secondary" onClick={() => prevStep()} {...tid.back} />
            <Button size="lg" label={saveLabel} disabled={disabled} onClick={onSave} {...tid.save} />
          </>
        ) : (
          <>
            <Button size="lg" label="Save & exit" variant="secondary" onClick={onSave} {...tid.saveAndExit} />
            <Button size="lg" label="Next" disabled={disabled} onClick={() => nextStep()} {...tid.next} />
          </>
        )}
      </div>
    </StepActions>
  );
}
